import { ExtractorResult } from "../../../common/entities/type";
import {
  formatRangeString,
  isNumericString,
  isRangeString,
} from "../store/helper";

/**
 * Normalisation of biomarker extractor results, shared by the upload
 * extractor modal and the Keragon "Sync EHR data" modal (whose result poll
 * returns the same per-row structure, plus a `keragonId`).
 *
 * Lifted verbatim from BiomarkerExtractorModal so both flows classify a
 * detected value the same way.
 */

export type BiomarkerStatus =
  | "saveable"
  | "qualitative"
  | "range"
  | "rejectedUnit"
  | "unmatched";

// Catalog metadata (from GetAllBiomarkers) needed to classify a value.
export type CatalogInfo = { qualitative?: boolean; qoptions?: string[] };

// Internal, UI-friendly representation of an extracted biomarker. Built once on
// arrival so the rest of the UI never has to deal with the raw response quirks
// (the list_unit vs unit split, the absent/null convertedValue states).
export type NormalizedBiomarker = {
  key: number; // original index — stable id for edit/delete
  biomarkerID: string | null;
  name: string;
  listName: string | null;
  displayName: string; // listName ?? name
  status: BiomarkerStatus;
  displayValue: string; // value shown and edited in the field
  displayUnit: string; // unit shown next to the value (and saved)
  originalValue: string | null; // raw detected value (converted only)
  originalUnit: string | null; // detected unit (converted only)
  qoptions?: string[]; // qualitative only — the select options
  keragonId: string | null; // Keragon only — saved as the reading's batchId
};

// Statuses that are persisted on save (numeric, qualitative, and read-only
// imported ranges). rejectedUnit/unmatched are shown only as notices.
export const PERSISTED_STATUSES: BiomarkerStatus[] = [
  "saveable",
  "qualitative",
  "range",
];
export const isPersisted = (item: NormalizedBiomarker) =>
  PERSISTED_STATUSES.includes(item.status);

/** id → qualitative/qoptions lookup from the GetAllBiomarkers catalog. */
export const buildCatalogById = (
  biomarkerList:
    | { id: string; qualitative?: boolean; qoptions?: string[] }[]
    | null
    | undefined
): Map<string, CatalogInfo> => {
  const map = new Map<string, CatalogInfo>();
  (biomarkerList ?? []).forEach((b) => {
    if (b?.id) map.set(b.id, { qualitative: b.qualitative, qoptions: b.qoptions });
  });
  return map;
};

export const normalizeResults = (
  results: ExtractorResult[],
  catalogById: Map<string, CatalogInfo>
): NormalizedBiomarker[] =>
  results
    .map((r, index) => {
      const listUnit = r.list_unit;
      const hasId = !!r.biomarkerID;
      const needsConversion = r.unit_match === false;
      // convertedValue may be a number OR a string (numeric like "6980.00" or a
      // range like "<0.05" / "150000-410000"); it's "converted" as long as the
      // backend returned a non-empty value.
      const converted =
        r.convertedValue !== null &&
        r.convertedValue !== undefined &&
        String(r.convertedValue).trim() !== "";
      const catalog = r.biomarkerID
        ? catalogById.get(r.biomarkerID)
        : undefined;

      let status: BiomarkerStatus;
      if (!hasId || r.matched === false) {
        // No DB id, or the extractor explicitly flagged it as not matched to
        // our database. Checked FIRST so a genuinely unmatched biomarker is
        // reported as "not matched", not mislabelled downstream.
        status = "unmatched";
      } else if (catalog?.qualitative) {
        // Qualitative biomarkers carry a string value (no meaningful unit
        // conversion). Checked BEFORE the unit logic so values like "Nil" with
        // a unit mismatch aren't wrongly bucketed as a failed conversion.
        status = "qualitative";
      } else if (needsConversion && !converted) {
        // Unit differs from ours and the backend couldn't convert it. Checked
        // before the range branch so a range we can't unit-convert is reported
        // as rejected rather than saved with the wrong-unit bounds.
        status = "rejectedUnit";
      } else if (isRangeString(r.value)) {
        // A range-style detected value (e.g. "<2.0"). Rendered read-only but
        // still saved (range fields are otherwise Junction-only).
        status = "range";
      } else if (!isNumericString(converted ? r.convertedValue : r.value)) {
        // Non-numeric, non-qualitative, non-range value (e.g. "Nil") — can't be
        // saved as a number, so surface it in the "couldn't convert" notice.
        status = "rejectedUnit";
      } else {
        status = "saveable";
      }

      // Unmatched biomarkers have no canonical list name, so show the detected
      // name; matched ones use our DB list name (falling back to the detected
      // name only if it's somehow missing).
      const displayName =
        status === "unmatched" ? r.name : r.list_name ?? r.name;

      let displayValue = r.value;
      let displayUnit = r.unit;
      let originalValue: string | null = null;
      let originalUnit: string | null = null;

      if (status === "qualitative") {
        // Qualitative has no unit. Pre-select the detected value only when it
        // matches a known option (case-insensitive); otherwise force a choice.
        const options = catalog?.qoptions ?? [];
        const match = options.find(
          (o) => o.trim().toLowerCase() === r.value.trim().toLowerCase()
        );
        displayValue = match ?? "";
        displayUnit = "";
      } else if (needsConversion && converted) {
        // Converted numeric OR range value, expressed in our DB (list) unit.
        displayValue = String(r.convertedValue);
        displayUnit = listUnit;
        // Keep the raw detected value + unit; the note renders the value through
        // LocalizedNumber so its separator mirrors the input field exactly.
        originalValue = r.value;
        originalUnit = r.unit;
      }

      // Drop insignificant trailing zeros so the backend's 2-decimal format
      // ("233.00") shows as "233" — for both plain numbers and each bound of a
      // range ("90.00-120.00" → "90-120"). Qualitative values are left as-is.
      if (isNumericString(displayValue)) {
        displayValue = String(Number(displayValue));
      } else if (status === "range") {
        displayValue = formatRangeString(displayValue);
      }

      return {
        key: index,
        biomarkerID: r.biomarkerID,
        name: r.name,
        listName: r.list_name,
        displayName,
        status,
        displayValue,
        displayUnit,
        originalValue,
        originalUnit,
        qoptions: status === "qualitative" ? catalog?.qoptions ?? [] : undefined,
        keragonId:
          r.keragonId === null || r.keragonId === undefined
            ? null
            : String(r.keragonId),
      };
    })
    // Skip biomarkers we can't label at all — no DB list name and no detected
    // name means there's nothing meaningful to show or save.
    .filter((item) => Boolean(item.displayName));
